import React, { ErrorInfo, ReactNode } from 'react';
import { T } from '@tolgee/react';
import { SypacButton, SypacText } from '@sypac/component-library-react';

interface ErrorBoundaryProps {
	children: ReactNode;
	fallback?: ReactNode;
}

interface ErrorBoundaryState {
	hasError: boolean;
}

/**
 * Without a boundary any render error unmounts the whole app and leaves a
 * blank white page. This keeps the error visible, logged, and recoverable.
 */
class ErrorBoundary extends React.Component<
	ErrorBoundaryProps,
	ErrorBoundaryState
> {
	state: ErrorBoundaryState = { hasError: false };

	static getDerivedStateFromError(): ErrorBoundaryState {
		return { hasError: true };
	}

	componentDidCatch(error: Error, info: ErrorInfo) {
		console.error('Unhandled render error', error, info?.componentStack);
	}

	reload = () => {
		window.location.reload();
	};

	render() {
		if (!this.state.hasError) {
			return this.props.children;
		}
		if (this.props.fallback !== undefined) {
			return this.props.fallback;
		}
		return (
			<section
				role="alert"
				className="w-full h-screen flex justify-center items-center"
			>
				<div className="w-[339px] flex flex-col text-center gap-[40px] box-border">
					<SypacText variant="body-regular-medium">
						<p className="text-2xl text-gray-80">
							<T keyName="errorBoundary.title">Something went wrong</T>
						</p>
					</SypacText>
					<SypacButton variant="secondary" size="small">
						<button
							type="button"
							className="py-[9px] border-gray-10 rounded-lg transition hover:border-gray-40"
							onClick={this.reload}
						>
							<SypacText variant="body-regular-medium">
								<p>
									<T keyName="errorBoundary.reload">Reload page</T>
								</p>
							</SypacText>
						</button>
					</SypacButton>
				</div>
			</section>
		);
	}
}

export default ErrorBoundary;
